import _ from 'lodash'
import _uniq from 'lodash-bound/uniq'
import { decorator as reusePromise } from 'reuse-promise'
import * as errors from '../errors'
import BaseService from './BaseService'

export default class ForumsService extends BaseService {
  mounted() {
    this._fetched = false
  }

  dispose() {
    this._fetched = false
  }

  async create({
    avatarFile,
    description,
    memberIds = [],
    metadata,
    name,
    organizationId,
    replayHistory = true,
    senderId = this.host.currentUserId,
  }) {
    this.host.requireUser()
    if (!organizationId) throw new errors.ValidationError('organizationId', 'required')

    const users =
      (await Promise.all([senderId, ...memberIds]
        .map(this.host.roles.__resolveRoleId)
        .filter(Boolean)
        .map(userId => this._resolveModelIdWithTypes(userId, 'user'))
        .map(userId => this.host.users.findOrCreate(userId, { organizationId }))
      ))
      ::_uniq()

    memberIds = users.map(userId => this._resolveModelIdWithTypes(userId, 'user'))

    const response = await this.host.api.groups.create({
      avatarFile,
      description,
      excludeCreator: false,
      isPublic: true,
      memberIds,
      name,
      organizationId,
      replayHistory,
    })

    let forum
    if (this.config.condensedReplays && response.entity) {
      forum = this.host.conversations.__injectCounterParty({
        conversationId: response.conversation_id,
        entityAttrs: response.entity,
        entityType: 'group',
        hasCurrentUser: true,
        organizationId: response.organization_id,
      })
    } else {
      forum = this.host.models.Group.inject(response)
    }

    if (metadata) {
      await this.host.metadata.update(forum.id, metadata, organizationId)
    }

    return forum
  }

  @reusePromise()
  async find(id: string, options={}) {
    options = _.defaults(options, {
      bypassCache: false,
      ignoreNotFound: false,
    })
    this.host.requireUser()

    let forum = null

    if (!options.bypassCache) {
      forum = this.host.models.Group.get(id)

      if (forum && !forum.$placeholder) {
        await this.host.groups.__ensureEntitiesLoaded(forum.id, options)

        return forum
      }
    }

    const data = await this.host.groups.__find(id)
    forum = this.host.models.Group.get(id)

    if (!data) {
      if (!options.ignoreNotFound) {
        throw new errors.NotFoundError(this.host.models.Group.name, id)
      }
      return forum
    }

    if (forum) {
      await this.host.groups.__ensureEntitiesLoaded(forum.id, options)
    }

    return forum
  }

  @reusePromise()
  async findAll({ bypassCache=false }={}) {
    if (!bypassCache && this._fetched) return this.getAll()

    let organizations = this.host.models.Organization.getAll()
    if (organizations.length === 0) {
      organizations = await this.host.organizations.findAll()
    }

    const { results } = await this.host.search.query({
      types: ['forum'],
      organizationIds: organizations.map(o => o.id),
      query: { displayName: '' },
      followContinuations: true,
    })

    const forums = results.map(searchResult => searchResult.entity)
    this._fetched = true

    return forums
  }

  getAll() {
    return this.host.models.Group
      .getAll()
      .filter(group => group.isPublic)
  }

  getById(id: string) {
    return this.host.models.Group.get(id)
  }

  async join(id: string|Object) {
    return this.host.groups.addMembers(id, [this.host.currentUserId])
  }
}
